import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import type { DataTableProps, TableState } from 'components/data-table/types';
import { useAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

type ServerDrivenTableProps<Row extends Record<string, unknown>> = Pick<
  DataTableProps<Row>,
  | 'data'
  | 'isLoading'
  | 'error'
  | 'scroll'
  | 'onPaginationChange'
  | 'onSortChange'
  | 'onGlobalSearchChange'
>;

interface ServerDrivenTable<Row extends Record<string, unknown>> {
  results: Row[];
  totalCount: number;
  /** Re-post the current params (e.g. after a create/delete) to refresh. */
  reload: () => void;
  tableProps: ServerDrivenTableProps<Row>;
}

export function useServerDrivenTable<
  Row extends Record<string, unknown>,
  Params extends SearchParams = SearchParams,
>({
  href,
  defaultParams,
}: {
  href: string;
  defaultParams: Params;
}): ServerDrivenTable<Row> {
  const { searchAtom } = useMemo(
    () => searchAtomPair<Row, Params>({ href }),
    [href]
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const [params, setParams] = useState<Params>(defaultParams);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const onTableStateChange = useCallback(
    (state: TableState) => {
      const sort = state.sorting[0];
      setParams((current) => {
        const next = {
          ...current,
          page: state.page,
          pageSize: state.pageSize,
          sortAttribute: sort ? sort.id : defaultParams.sortAttribute,
          sortDirection: sort
            ? sort.desc
              ? 'DESC'
              : 'ASC'
            : defaultParams.sortDirection,
          text: state.globalFilter || undefined,
        } as Params;
        // Preserve the reference when nothing changed so the effect above
        // doesn't re-post identical params.
        return isEqual(current, next) ? current : next;
      });
    },
    [defaultParams.sortAttribute, defaultParams.sortDirection]
  );

  const reload = useCallback(
    () => setParams((current) => ({ ...current })),
    []
  );

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  return {
    results,
    totalCount,
    reload,
    tableProps: {
      data: results,
      isLoading: searchResult.loading ?? false,
      error: searchResult.error ?? null,
      scroll: { mode: 'paginated', serverDriven: true, totalCount },
      onPaginationChange: onTableStateChange,
      onSortChange: onTableStateChange,
      onGlobalSearchChange: onTableStateChange,
    },
  };
}
